import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PORTAL_CONFIG } from "@/app/router/portalConfig";
import { PORTAL_HOME } from "@/app/router/routes";
import { useAuth } from "@/features/auth/AuthProvider";
import { cn } from "@/lib/utils";

/**
 * Shown only to users with several portals (e.g. a doctor who is also an
 * admin). Navigating to the other portal's home switches the session context.
 */
export function PortalSwitcher({ className }: { className?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me } = useAuth();
  if (!me || me.portals.length < 2) return null;

  return (
    <div role="tablist" aria-label={t("portal.switch")} className={cn("inline-flex rounded-xl border bg-muted/60 p-1", className)}>
      {me.portals.map((portal) => {
        const Icon = PORTAL_CONFIG[portal].icon;
        const active = me.activePortal === portal;
        return (
          <button
            key={portal}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => !active && navigate(PORTAL_HOME[portal])}
            className={cn(
              "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-muted-foreground transition-all",
              active && "bg-card text-primary shadow-soft",
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden />
            {t(PORTAL_CONFIG[portal].labelKey)}
          </button>
        );
      })}
    </div>
  );
}
